/*
 * sv-header.js
 * ------------
 * Header behaviors for sv-header.liquid. No framework deps. Binds on
 * DOMContentLoaded.
 *
 * Three independent modules:
 *   1. wireMobileDrawer — push-panel drawer (Wave 8b). Root → subpanel
 *      slide with back button, focus trap, scroll-lock, Esc step-back.
 *   2. wireMega          — desktop mega-menu (Wave 8a). Hover triggers
 *      with 180ms close forgiveness, keyboard arrow navigation.
 *   3. wireViewportGuard — force-close anything mid-resize across the
 *      900px breakpoint so desktop never shows mobile drawer, and mobile
 *      never shows a stranded mega panel.
 */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    wireMobileDrawer();
    wireMega();
  });

  /* ============================================================
   * 1. Mobile drawer (Wave 8b — push-panel)
   * ------------------------------------------------------------
   * Markup: snippets/sv-mobile-drawer.liquid
   * Scrim:  [data-sv-mobile-scrim]
   * Drawer: [data-sv-mobile-drawer]
   * Hamburger: [data-sv-header-toggle]  (re-used from Wave 7a)
   * Close: [data-sv-mobile-close]
   * Sub triggers: [data-sv-mobile-sub="<key>"]
   * Subpanels: [data-sv-mobile-subpanel="<key>"]
   * Back: [data-sv-mobile-back]
   *
   * Behaviors:
   *   · Hamburger toggle
   *   · Scrim click close
   *   · Esc: subpanel open → back to root; root only → close drawer
   *   · Link tap inside drawer → close
   *   · Body scroll-lock while open; <body data-mdrawer-open="true">
   *     lowers sv-announce + sv-header z-index below the scrim
   *   · Focus trap: Tab cycles among focusable children of the drawer
   *   · 900px+ viewport → force-close (CSS already hides, JS clears state)
   * ============================================================ */
  function wireMobileDrawer() {
    var toggle = document.querySelector('[data-sv-header-toggle]');
    var drawer = document.querySelector('[data-sv-mobile-drawer]');
    var scrim = document.querySelector('[data-sv-mobile-scrim]');
    if (!toggle || !drawer || !scrim) return;

    var closeBtn = drawer.querySelector('[data-sv-mobile-close]');
    var rootPanel = drawer.querySelector('[data-sv-mobile-root]');
    var panelsContainer = drawer.querySelector('.mdrawer__panels');
    var subTriggers = Array.prototype.slice.call(drawer.querySelectorAll('[data-sv-mobile-sub]'));
    var subPanels = Array.prototype.slice.call(drawer.querySelectorAll('[data-sv-mobile-subpanel]'));
    var backBtns = Array.prototype.slice.call(drawer.querySelectorAll('[data-sv-mobile-back]'));

    var subByKey = {};
    subPanels.forEach(function (p) {
      subByKey[p.getAttribute('data-sv-mobile-subpanel')] = p;
    });

    var isOpen = false;
    var openSubKey = null;
    var lastFocus = null;

    function setRootPushed(pushed) {
      if (!rootPanel) return;
      rootPanel.setAttribute('data-pushed', pushed ? 'true' : 'false');
    }

    /**
     * Defensive guard against browser-initiated scroll-into-view shifting
     * the absolutely-positioned .mdrawer__panel children off-screen. The
     * panels container has scrollWidth ≈ 2× clientWidth on this markup
     * (leftover from an earlier flex-row layout), so when focus lands on
     * a newly-activated sub panel the browser auto-scrolls panels by up
     * to ~284px to "bring focus into view" — which visually shifts every
     * absolute child by the same amount in the opposite direction and
     * makes the sub panel appear to render off the drawer's left edge.
     *
     * Primary fix is `overflow: clip` on .mdrawer__panels in CSS, which
     * forbids programmatic scroll. This JS is the fallback for older
     * browsers (Safari < 16, Chrome < 90) that don't support `clip`.
     */
    function resetPanelsScroll() {
      if (panelsContainer) {
        if (panelsContainer.scrollLeft) panelsContainer.scrollLeft = 0;
        if (panelsContainer.scrollTop)  panelsContainer.scrollTop  = 0;
      }
    }

    function openSub(key) {
      if (openSubKey === key) return;
      // close any currently open sub before opening the new one
      if (openSubKey) {
        var prev = subByKey[openSubKey];
        if (prev) prev.setAttribute('data-open', 'false');
      }
      var panel = subByKey[key];
      if (!panel) return;
      panel.setAttribute('data-open', 'true');
      setRootPushed(true);
      openSubKey = key;
      // Reset scroll positions so the subpanel always opens at its
      // natural top-left. scroll-snap + flex-column interactions can
      // leave these at non-zero values otherwise.
      var scroll = panel.querySelector('.mdrawer__subscroll');
      if (scroll) scroll.scrollTop = 0;
      var intent = panel.querySelector('.mdrawer__intent');
      if (intent) intent.scrollLeft = 0;
      // Panels-container scroll-guard (see resetPanelsScroll docs above).
      // Run on the next frame so it wins over any browser auto-scroll
      // that happens as focus lands in the newly-activated panel.
      requestAnimationFrame(resetPanelsScroll);
      // Move focus to the back button of the new subpanel
      var back = panel.querySelector('[data-sv-mobile-back]');
      if (back) {
        requestAnimationFrame(function () { try { back.focus(); } catch (_) {} });
      }
    }

    function closeSub() {
      if (!openSubKey) return;
      var panel = subByKey[openSubKey];
      if (panel) panel.setAttribute('data-open', 'false');
      setRootPushed(false);
      requestAnimationFrame(resetPanelsScroll);
      // Return focus to the trigger that opened this subpanel
      var trig = subTriggers.find(function (t) {
        return t.getAttribute('data-sv-mobile-sub') === openSubKey;
      });
      openSubKey = null;
      if (trig) {
        requestAnimationFrame(function () { try { trig.focus(); } catch (_) {} });
      }
    }

    function open() {
      if (isOpen) return;
      isOpen = true;
      lastFocus = document.activeElement;
      drawer.setAttribute('data-open', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      scrim.setAttribute('data-open', 'true');
      scrim.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', document.documentElement.lang === 'fr' ? 'Fermer le menu' : 'Close menu');
      document.body.setAttribute('data-mdrawer-open', 'true');
      document.body.classList.add('sv-header-scroll-lock');
      // Clean panels scroll state when drawer first opens
      resetPanelsScroll();
      // Focus the close button after transition start
      if (closeBtn) {
        requestAnimationFrame(function () { try { closeBtn.focus(); } catch (_) {} });
      }
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      drawer.setAttribute('data-open', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      scrim.setAttribute('data-open', 'false');
      scrim.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', document.documentElement.lang === 'fr' ? 'Ouvrir le menu' : 'Open menu');
      document.body.removeAttribute('data-mdrawer-open');
      document.body.classList.remove('sv-header-scroll-lock');
      // Reset subpanel after transition finishes
      setTimeout(function () {
        if (!isOpen && openSubKey) {
          var panel = subByKey[openSubKey];
          if (panel) panel.setAttribute('data-open', 'false');
          setRootPushed(false);
          openSubKey = null;
        }
      }, 300);
      try { if (lastFocus && lastFocus.focus) lastFocus.focus(); } catch (_) {}
    }

    // Wire controls
    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      isOpen ? close() : open();
    });
    scrim.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', function (e) { e.preventDefault(); close(); });

    subTriggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        var key = t.getAttribute('data-sv-mobile-sub');
        openSub(key);
      });
    });
    backBtns.forEach(function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        closeSub();
      });
    });

    // Close drawer on any <a href> tap inside (navigation follows)
    drawer.addEventListener('click', function (e) {
      var t = e.target;
      var a = t && t.closest ? t.closest('a[href]') : null;
      if (a && !a.hasAttribute('data-sv-mobile-back')) close();
    });

    // Escape: subpanel → root; root-only → close
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !isOpen) return;
      e.preventDefault();
      if (openSubKey) closeSub();
      else close();
    });

    // Focus trap — Tab cycles among focusable elements inside the drawer
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !isOpen) return;
      var focusables = getFocusable(drawer);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Viewport change: force-close at desktop width (CSS hides drawer but
    // we still need to reset scroll-lock + aria state).
    var mq = window.matchMedia('(min-width: 901px)');
    var onChange = function (ev) { if (ev.matches && isOpen) close(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  }

  function getFocusable(root) {
    var sel = [
      'a[href]:not([disabled])',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.prototype.slice.call(root.querySelectorAll(sel)).filter(function (el) {
      // skip elements that are visibility:hidden or display:none
      var rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
  }

  /* ============================================================
   * 2. Desktop mega-menu (Wave 8a)
   * ------------------------------------------------------------
   * Triggers: [data-sv-mega-trigger="<key>"]
   * Panels:   [data-sv-mega-panel="<key>"]
   * Behaviors: hover+180ms close forgiveness, click toggle,
   *            Escape + Enter/Space + Arrow Left/Right, focus mgmt.
   * ============================================================ */
  function wireMega() {
    var triggers = Array.prototype.slice.call(
      document.querySelectorAll('[data-sv-mega-trigger]')
    );
    var panels = Array.prototype.slice.call(
      document.querySelectorAll('[data-sv-mega-panel]')
    );
    if (!triggers.length || !panels.length) return;

    var panelByKey = {};
    panels.forEach(function (p) {
      panelByKey[p.getAttribute('data-sv-mega-panel')] = p;
    });

    var openKey = null;
    var closeTimer = null;
    var mq = window.matchMedia('(max-width: 900px)');

    function isMobile() { return mq.matches; }

    function setState(key, open) {
      var trig = triggers.find(function (t) { return t.getAttribute('data-sv-mega-trigger') === key; });
      var panel = panelByKey[key];
      if (!trig || !panel) return;
      if (open) {
        trig.setAttribute('aria-expanded', 'true');
        panel.setAttribute('data-open', 'true');
        panel.setAttribute('aria-hidden', 'false');
      } else {
        trig.setAttribute('aria-expanded', 'false');
        panel.setAttribute('data-open', 'false');
        panel.setAttribute('aria-hidden', 'true');
      }
    }

    function openMega(key) {
      if (isMobile()) return;
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      if (openKey && openKey !== key) setState(openKey, false);
      openKey = key;
      setState(key, true);
    }

    function closeMega(immediate) {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      if (immediate) {
        if (openKey) setState(openKey, false);
        openKey = null;
      } else {
        closeTimer = setTimeout(function () {
          if (openKey) setState(openKey, false);
          openKey = null;
          closeTimer = null;
        }, 180);
      }
    }

    function cancelClose() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    }

    triggers.forEach(function (t, idx) {
      var key = t.getAttribute('data-sv-mega-trigger');
      t.setAttribute('aria-haspopup', 'true');
      t.setAttribute('aria-expanded', 'false');
      t.setAttribute('aria-controls', 'sv-mega-' + key);

      t.addEventListener('mouseenter', function () { openMega(key); });
      t.addEventListener('mouseleave', function () { closeMega(); });
      t.addEventListener('focus', function () { if (!isMobile()) openMega(key); });
      t.addEventListener('click', function () {
        // Let the visible top-level nav text behave like a normal link.
        // The mega menu still opens on hover/focus; keyboard users can use
        // Space to open the panel without navigating.
        if (isMobile()) return;
        closeMega(true);
      });
      t.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          var next = triggers[(idx + 1) % triggers.length];
          if (next) next.focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          var prev = triggers[(idx - 1 + triggers.length) % triggers.length];
          if (prev) prev.focus();
        } else if (e.key === 'Escape' && openKey) {
          e.preventDefault();
          closeMega(true);
          t.focus();
        } else if ((e.key === 'Enter' || e.key === ' ') && openKey !== key) {
          e.preventDefault();
          openMega(key);
        }
      });
    });

    panels.forEach(function (p) {
      p.addEventListener('mouseenter', cancelClose);
      p.addEventListener('mouseleave', function () { closeMega(); });
      p.addEventListener('click', function (e) {
        if (e.target && e.target.closest && e.target.closest('a[href]')) {
          closeMega(true);
        }
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openKey) {
        var key = openKey;
        closeMega(true);
        var trig = triggers.find(function (t) {
          return t.getAttribute('data-sv-mega-trigger') === key;
        });
        if (trig) trig.focus();
      }
    });

    var onMqChange = function (ev) { if (ev.matches && openKey) closeMega(true); };
    if (mq.addEventListener) mq.addEventListener('change', onMqChange);
    else if (mq.addListener) mq.addListener(onMqChange);
  }
})();
